/**
 * Company-scoped reads for the parallel-bootstrap prefetch (`layouts/customer/parallel-bootstrap-prefetch`).
 *
 * Most customer pages scope their `useCustomerQuery` cache by the store's company id, which a hard
 * load only learns from the bootstrap the prefetch is meant to overlap. The request itself does not
 * need the id (the API Worker answers for the caller's company), so it starts at once and is filed
 * under the company's cache key the moment the bootstrap puts the company in the store. That is
 * before the layout's gate opens and the page mounts, so the page finds the read in flight or
 * answered instead of starting its own.
 */
import { prefetchCustomerQuery, startSharedCustomerRequest } from '@/hooks/useCustomerQuery'
import { useGeneralStore } from '@/store/useGeneralStore'

/** How long a prefetch waits for the bootstrap to name the company before it gives up. */
const COMPANY_WAIT_MS = 20_000

export function currentCompanyId(): string | null {
  return useGeneralStore.getState().companyDetail?.company_id || null
}

/**
 * The company id once the bootstrap has put it in the store; null when the bootstrap answered
 * without one, failed, or took longer than `timeoutMs`.
 */
export function whenCompanyKnown(timeoutMs = COMPANY_WAIT_MS): Promise<string | null> {
  const known = currentCompanyId()
  if (known) return Promise.resolve(known)
  // Already settled with no company (an account still in onboarding): nothing is coming.
  const status = useGeneralStore.getState().customerBootstrapStatus
  if (status === 'loaded' || status === 'failed') return Promise.resolve(null)
  return new Promise((resolve) => {
    let settled = false
    const finish = (companyId: string | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      resolve(companyId)
    }
    const timer = setTimeout(() => finish(null), timeoutMs)
    const unsubscribe = useGeneralStore.subscribe((state) => {
      const companyId = state.companyDetail?.company_id
      if (companyId) {
        finish(companyId)
        return
      }
      // The layout marks the bootstrap settled just before it stores the company: look again
      // once that same task is done before deciding there is none.
      if (state.customerBootstrapStatus === 'loaded' || state.customerBootstrapStatus === 'failed') {
        setTimeout(() => finish(currentCompanyId()), 0)
      }
    })
  })
}

/**
 * Starts `queryFn` now and files it under `cacheKey` scoped by the company once the company is
 * known (at once when it already is). Resolves undefined when there is no company to file it under.
 *
 * The read is shared under `requestKey`, which the page's own `useCustomerQuery` names too: a second
 * prefetch (the boot one and the layout's both run on a hard load) or a page that mounts before the
 * read is filed joins it instead of sending another.
 */
export async function prefetchCompanyScopedQuery<T>({
  cacheKey,
  userScopeKey,
  requestKey,
  queryFn,
}: {
  cacheKey: string
  userScopeKey: string
  requestKey: string
  queryFn: () => Promise<T>
}): Promise<T | undefined> {
  const known = currentCompanyId()
  if (known) return prefetchCustomerQuery({ cacheKey, userScopeKey, scopeKey: known, requestKey, queryFn })

  const request = startSharedCustomerRequest({ cacheKey, userScopeKey, requestKey, queryFn })
  // Nobody may ever await it (no company); its failure is the page's own read's to report.
  request.catch(() => {})
  const companyId = await whenCompanyKnown()
  if (!companyId) return undefined
  return prefetchCustomerQuery({ cacheKey, userScopeKey, scopeKey: companyId, requestKey, queryFn: () => request })
}
